import { customTeamPageAllowed } from "@/lib/team-permissions";
import { cashierPageAllowed } from "@/lib/cashier-navigation";
import { useActiveTeam } from "@/lib/team-context";
import { useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import {
  ArrowDownLeft,
  ArrowLeftRight,
  ArrowUpRight,
  Banknote,
  BarChart3,
  Boxes,
  BookOpen,
  BookText,
  Box,
  Building2,
  Calculator,
  CalendarDays,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  FileBarChart2,
  FileEdit,
  FileMinus,
  FileSearch,
  FileSpreadsheet,
  FileUp,
  FileSignature,
  FileText,
  FolderOpen,
  HandCoins,
  Home,
  Inbox,
  Landmark,
  Layers,
  Percent,
  Receipt,
  ReceiptText,
  Scale,
  Settings,
  ShoppingCart,
  TrendingUp,
  UserMinus,
  Users,
  Wallet,
  Warehouse,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Separator } from "@/components/ui/separator";
import { useActiveCompany } from "@/lib/company-context";
import { cn } from "@/lib/cn";
import { isBillingEnabled } from "@/lib/runtime-env";

type NavLink = {
  to: string;
  labelKey: string;
  icon: typeof Home;
};

const links: NavLink[] = [
  { to: "/dashboard", labelKey: "nav.dashboard", icon: Home },
  { to: "/teams", labelKey: "nav.teams", icon: Users },
  { to: "/companies", labelKey: "nav.companies", icon: Building2 },
  { to: "/subscription", labelKey: "nav.subscription", icon: ReceiptText },
  { to: "/settings", labelKey: "nav.settings", icon: Settings },
];

type CompanyNavLink = {
  /** The path is rendered with the active companyId substituted. */
  buildTo: (companyId: string) => string;
  /** The TanStack Router static "to" pattern for active-match matching. */
  matchPrefix: (companyId: string) => string;
  labelKey: string;
  icon: typeof Home;
  isActive?: (pathname: string, companyId: string) => boolean;
};

const ventasLinks: CompanyNavLink[] = [
  {
    buildTo: (id) => `/companies/${id}/pos`,
    matchPrefix: (id) => `/companies/${id}/pos`,
    labelKey: "nav.pos",
    icon: ReceiptText,
    isActive: (pathname, id) => {
      const base = `/companies/${id}/pos`;
      if (pathname === base || pathname === `${base}/`) return true;
      if (pathname.startsWith(`${base}/ajustes`)) return false;
      return pathname.startsWith(`${base}/`);
    },
  },
  {
    buildTo: (id) => `/companies/${id}/pos/ajustes`,
    matchPrefix: (id) => `/companies/${id}/pos/ajustes`,
    labelKey: "nav.posAjustes",
    icon: FileEdit,
  },
  {
    buildTo: (id) => `/companies/${id}/facturas`,
    matchPrefix: (id) => `/companies/${id}/facturas`,
    labelKey: "nav.facturas",
    icon: FileSignature,
  },
  {
    buildTo: (id) => `/companies/${id}/notas-credito`,
    matchPrefix: (id) => `/companies/${id}/notas-credito`,
    labelKey: "nav.notasCredito",
    icon: ArrowDownLeft,
  },
  {
    buildTo: (id) => `/companies/${id}/notas-debito`,
    matchPrefix: (id) => `/companies/${id}/notas-debito`,
    labelKey: "nav.notasDebito",
    icon: ArrowUpRight,
  },
];

const comprasLinks: CompanyNavLink[] = [
  {
    buildTo: (id) => `/companies/${id}/compras`,
    matchPrefix: (id) => `/companies/${id}/compras`,
    labelKey: "nav.compras.facturas",
    icon: Receipt,
  },
  {
    buildTo: (id) => `/companies/${id}/recibidas`,
    matchPrefix: (id) => `/companies/${id}/recibidas`,
    labelKey: "nav.compras.recibidas",
    icon: Inbox,
  },
  {
    buildTo: (id) => `/companies/${id}/documentos-soporte`,
    matchPrefix: (id) => `/companies/${id}/documentos-soporte`,
    labelKey: "nav.documentosSoporte",
    icon: FileMinus,
  },
];

const tesoreriaLinks: CompanyNavLink[] = [
  {
    buildTo: (id) => `/companies/${id}/recibos`,
    matchPrefix: (id) => `/companies/${id}/recibos`,
    labelKey: "nav.tesoreria.recibos",
    icon: HandCoins,
  },
  {
    buildTo: (id) => `/companies/${id}/egresos`,
    matchPrefix: (id) => `/companies/${id}/egresos`,
    labelKey: "nav.tesoreria.egresos",
    icon: Banknote,
  },
  {
    buildTo: (id) => `/companies/${id}/bancos`,
    matchPrefix: (id) => `/companies/${id}/bancos`,
    labelKey: "nav.bancos",
    icon: Landmark,
  },
];

const reportesLinks: CompanyNavLink[] = [
  {
    buildTo: (id) => `/companies/${id}/reportes/estado-resultados`,
    matchPrefix: (id) => `/companies/${id}/reportes/estado-resultados`,
    labelKey: "nav.reportes.estado_resultados",
    icon: TrendingUp,
  },
  {
    buildTo: (id) => `/companies/${id}/reportes/balance-general`,
    matchPrefix: (id) => `/companies/${id}/reportes/balance-general`,
    labelKey: "nav.reportes.balance_general",
    icon: Scale,
  },
  {
    buildTo: (id) => `/companies/${id}/reportes/iva`,
    matchPrefix: (id) => `/companies/${id}/reportes/iva`,
    labelKey: "nav.reportes.iva",
    icon: Receipt,
  },
  {
    buildTo: (id) => `/companies/${id}/reportes/retenciones`,
    matchPrefix: (id) => `/companies/${id}/reportes/retenciones`,
    labelKey: "nav.reportes.retenciones",
    icon: Percent,
  },
];

const impuestosLinks: CompanyNavLink[] = [
  {
    buildTo: (id) => `/companies/${id}/impuestos`,
    matchPrefix: (id) => `/companies/${id}/impuestos`,
    labelKey: "nav.impuestos.calendario",
    icon: CalendarDays,
  },
  {
    buildTo: (id) => `/companies/${id}/impuestos/configuracion`,
    matchPrefix: (id) => `/companies/${id}/impuestos/configuracion`,
    labelKey: "nav.impuestos.configuracion",
    icon: Settings,
  },
  {
    buildTo: (id) => `/companies/${id}/impuestos/muisca`,
    matchPrefix: (id) => `/companies/${id}/impuestos/muisca`,
    labelKey: "nav.impuestos.muisca",
    icon: FileSearch,
  },
];

const contabilidadLinks: CompanyNavLink[] = [
  {
    buildTo: (id) => `/companies/${id}/contabilidad/diario`,
    matchPrefix: (id) => `/companies/${id}/contabilidad/diario`,
    labelKey: "nav.contabilidad.diario",
    icon: BookOpen,
  },
  {
    buildTo: (id) => `/companies/${id}/contabilidad/mayor`,
    matchPrefix: (id) => `/companies/${id}/contabilidad/mayor`,
    labelKey: "nav.contabilidad.mayor",
    icon: BookText,
  },
  {
    buildTo: (id) => `/companies/${id}/contabilidad/cuentas`,
    matchPrefix: (id) => `/companies/${id}/contabilidad/cuentas`,
    labelKey: "nav.contabilidad.cuentas",
    icon: Settings,
  },
  {
    buildTo: (id) => `/companies/${id}/contabilidad/saldos-iniciales`,
    matchPrefix: (id) => `/companies/${id}/contabilidad/saldos-iniciales`,
    labelKey: "nav.contabilidad.saldosIniciales",
    icon: FileSpreadsheet,
  },
  {
    buildTo: (id) => `/companies/${id}/importaciones`,
    matchPrefix: (id) => `/companies/${id}/importaciones`,
    labelKey: "nav.contabilidad.importaciones",
    icon: FileUp,
  },
];

const inventarioLinks: CompanyNavLink[] = [
  {
    buildTo: (id) => `/companies/${id}/inventario`,
    matchPrefix: (id) => `/companies/${id}/inventario`,
    labelKey: "nav.inventario.dashboard",
    icon: Layers,
  },
  {
    buildTo: (id) => `/companies/${id}/inventario/movimientos`,
    matchPrefix: (id) => `/companies/${id}/inventario/movimientos`,
    labelKey: "nav.inventario.movimientos",
    icon: ArrowLeftRight,
  },
  {
    buildTo: (id) => `/companies/${id}/inventario/bodegas`,
    matchPrefix: (id) => `/companies/${id}/inventario/bodegas`,
    labelKey: "nav.inventario.bodegas",
    icon: Warehouse,
  },
  {
    buildTo: (id) => `/companies/${id}/inventario/conteos`,
    matchPrefix: (id) => `/companies/${id}/inventario/conteos`,
    labelKey: "nav.inventario.conteos",
    icon: ClipboardCheck,
  },
  {
    buildTo: (id) => `/companies/${id}/inventario/reportes`,
    matchPrefix: (id) => `/companies/${id}/inventario/reportes`,
    labelKey: "nav.inventario.reportes",
    icon: FileBarChart2,
  },
];

const NAV_LABEL_FALLBACK: Record<string, string> = {
  "nav.inventario.title": "Inventario",
  "nav.inventario.dashboard": "Existencias",
  "nav.inventario.movimientos": "Kardex",
  "nav.inventario.bodegas": "Bodegas",
  "nav.inventario.conteos": "Conteos físicos",
  "nav.inventario.reportes": "Reportes",
  "nav.impuestos.title": "Impuestos",
  "nav.impuestos.calendario": "Calendario",
  "nav.impuestos.configuracion": "Configuracion",
  "nav.impuestos.muisca": "MUISCA",
  "nav.ventas.title": "Ventas",
  "nav.pos": "Documentos POS",
  "nav.posAjustes": "Ajustes POS",
  "nav.compras.title": "Compras",
  "nav.compras.facturas": "Facturas de compra",
  "nav.compras.recibidas": "Recibidas (RADIAN)",
  "nav.tesoreria.title": "Tesoreria",
  "nav.tesoreria.recibos": "Recibos de caja",
  "nav.tesoreria.egresos": "Egresos",
  "nav.cartera": "Cartera",
  "nav.archivos": "Archivos",
  "nav.contabilidad.importaciones": "Importar documentos",
};

const nominaLinks: CompanyNavLink[] = [
  {
    buildTo: (id) => `/companies/${id}/nomina/empleados`,
    matchPrefix: (id) => `/companies/${id}/nomina/empleados`,
    labelKey: "nav.nomina.empleados",
    icon: Users,
  },
  {
    buildTo: (id) => `/companies/${id}/nomina/liquidaciones`,
    matchPrefix: (id) => `/companies/${id}/nomina/liquidaciones`,
    labelKey: "nav.nomina.liquidaciones",
    icon: Calculator,
  },
  {
    buildTo: (id) => `/companies/${id}/nomina/payslips`,
    matchPrefix: (id) => `/companies/${id}/nomina/payslips`,
    labelKey: "nav.nomina.payslips",
    icon: FileText,
  },
  {
    buildTo: (id) => `/companies/${id}/nomina/ajustes`,
    matchPrefix: (id) => `/companies/${id}/nomina/ajustes`,
    labelKey: "nav.nomina.ajustes",
    icon: FileEdit,
  },
  {
    buildTo: (id) => `/companies/${id}/nomina/definitivas`,
    matchPrefix: (id) => `/companies/${id}/nomina/definitivas`,
    labelKey: "nav.nomina.definitivas",
    icon: UserMinus,
  },
];

const companyLinks: CompanyNavLink[] = [
  {
    buildTo: (id) => `/companies/${id}/terceros`,
    matchPrefix: (id) => `/companies/${id}/terceros`,
    labelKey: "nav.catalogos.terceros",
    icon: Users,
  },
  {
    buildTo: (id) => `/companies/${id}/productos`,
    matchPrefix: (id) => `/companies/${id}/productos`,
    labelKey: "nav.catalogos.productos",
    icon: Box,
  },
  {
    buildTo: (id) => `/companies/${id}/puc`,
    matchPrefix: (id) => `/companies/${id}/puc`,
    labelKey: "nav.catalogos.puc",
    icon: BookOpen,
  },
  {
    buildTo: (id) => `/companies/${id}/resoluciones`,
    matchPrefix: (id) => `/companies/${id}/resoluciones`,
    labelKey: "nav.catalogos.resoluciones",
    icon: FileText,
  },
];

type AppNavigationProps = {
  className?: string;
  onNavigate?: () => void;
};

/**
 * Shared authenticated navigation. Desktop renders this inside the persistent
 * sidebar; mobile renders the same tree inside the header drawer.
 */
export function AppNavigation({ className, onNavigate }: AppNavigationProps) {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { activeCompany } = useActiveCompany();
  const nominaActive = activeCompany
    ? pathname.startsWith(`/companies/${activeCompany.id}/nomina`)
    : false;
  const [nominaOpen, setNominaOpen] = useState<boolean>(false);
  const isNominaOpen = nominaOpen || nominaActive;

  const contabilidadActive = activeCompany
    ? pathname.startsWith(`/companies/${activeCompany.id}/contabilidad`)
    : false;
  const [contabilidadOpen, setContabilidadOpen] = useState<boolean>(false);
  const isContabilidadOpen = contabilidadOpen || contabilidadActive;

  const reportesActive = activeCompany
    ? pathname.startsWith(`/companies/${activeCompany.id}/reportes`)
    : false;
  const [reportesOpen, setReportesOpen] = useState<boolean>(false);
  const isReportesOpen = reportesOpen || reportesActive;

  const inventarioActive = activeCompany
    ? pathname.startsWith(`/companies/${activeCompany.id}/inventario`)
    : false;
  const [inventarioOpen, setInventarioOpen] = useState<boolean>(false);
  const isInventarioOpen = inventarioOpen || inventarioActive;

  const impuestosActive = activeCompany
    ? pathname.startsWith(`/companies/${activeCompany.id}/impuestos`)
    : false;
  const [impuestosOpen, setImpuestosOpen] = useState<boolean>(false);
  const isImpuestosOpen = impuestosOpen || impuestosActive;

  const startsWithAny = (prefixes: string[]): boolean =>
    activeCompany
      ? prefixes.some((p) =>
          pathname.startsWith(`/companies/${activeCompany.id}/${p}`),
        )
      : false;

  const ventasActive = startsWithAny([
    "pos",
    "facturas",
    "notas-credito",
    "notas-debito",
  ]);
  const [ventasOpen, setVentasOpen] = useState<boolean>(false);
  const isVentasOpen = ventasOpen || ventasActive;

  const comprasActive = startsWithAny([
    "compras",
    "recibidas",
    "documentos-soporte",
  ]);
  const [comprasOpen, setComprasOpen] = useState<boolean>(false);
  const isComprasOpen = comprasOpen || comprasActive;

  const tesoreriaActive = startsWithAny(["recibos", "egresos", "bancos"]);
  const [tesoreriaOpen, setTesoreriaOpen] = useState<boolean>(false);
  const isTesoreriaOpen = tesoreriaOpen || tesoreriaActive;

  const carteraActive = startsWithAny(["cartera"]);
  const archivosActive = startsWithAny(["archivos"]);

  /**
   * Resolve a sidebar label from the i18n catalog, falling back to a Spanish
   * default when the locale file hasn't been updated yet (the inventory keys
   * land in a follow-up i18n PR — using defaultValue avoids showing the raw
   * key while another agent is editing the locale JSON).
   */
  function navLabel(key: string): string {
    return t(key, { defaultValue: NAV_LABEL_FALLBACK[key] ?? key });
  }

  const currentTeam = useActiveTeam().activeTeam;
  const cashier = currentTeam?.role === "cashier";
  const permissions = currentTeam?.permissions;
  if (permissions !== undefined) {
    const candidates: CompanyNavLink[] = [
      ...ventasLinks,
      ...comprasLinks,
      ...tesoreriaLinks,
      ...reportesLinks,
      ...impuestosLinks,
      ...contabilidadLinks,
      ...inventarioLinks,
      ...nominaLinks,
      ...companyLinks,
      {
        buildTo: (id) => `/companies/${id}/cartera`,
        matchPrefix: (id) => `/companies/${id}/cartera`,
        labelKey: "nav.cartera",
        icon: Wallet,
      },
      {
        buildTo: (id) => `/companies/${id}/archivos`,
        matchPrefix: (id) => `/companies/${id}/archivos`,
        labelKey: "nav.archivos",
        icon: FolderOpen,
      },
    ];
    return (
      <nav
        className={cn("flex flex-col gap-3 p-3", className)}
        onClick={onNavigate}
      >
        <Link to="/companies">{t("nav.companies")}</Link>
        {activeCompany
          ? candidates
              .filter(
                (link) =>
                  customTeamPageAllowed(
                    link.buildTo(activeCompany.id),
                    permissions,
                  ) &&
                  (!cashier ||
                    cashierPageAllowed(link.buildTo(activeCompany.id))),
              )
              .map((link) => (
                <Link key={link.labelKey} to={link.buildTo(activeCompany.id)}>
                  {navLabel(link.labelKey)}
                </Link>
              ))
          : null}
      </nav>
    );
  }
  if (cashier)
    return (
      <nav
        className={cn("flex flex-col gap-3 p-3", className)}
        onClick={onNavigate}
      >
        <Link to="/companies">{t("nav.companies")}</Link>
        {activeCompany ? (
          <Link
            to="/companies/$companyId/facturas"
            params={{ companyId: activeCompany.id }}
          >
            {t("nav.facturas")}
          </Link>
        ) : null}
      </nav>
    );

  return (
    <nav
      className={cn("flex flex-1 flex-col gap-1 p-3", className)}
      onClick={(event) => {
        if (
          onNavigate &&
          event.target instanceof Element &&
          event.target.closest("a")
        ) {
          onNavigate();
        }
      }}
    >
      {links
        .filter((link) => isBillingEnabled() || link.to !== "/subscription")
        .map((link) => {
          const Icon = link.icon;
          const active =
            pathname === link.to || pathname.startsWith(link.to + "/");
          return (
            <Link
              key={link.to}
              to={link.to}
              className={cn(
                "flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                active
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
              )}
            >
              <Icon className="h-4 w-4" />
              {t(link.labelKey)}
            </Link>
          );
        })}

      {activeCompany ? (
        <>
          <Separator className="my-2" />

          {/* ── Ventas collapsible section ──────────────────────────── */}
          <button
            type="button"
            onClick={() => setVentasOpen((s) => !s)}
            className={cn(
              "flex items-center justify-between gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
              ventasActive
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
            )}
            aria-expanded={isVentasOpen}
          >
            <span className="flex items-center gap-2">
              <FileSignature className="h-4 w-4" />
              {navLabel("nav.ventas.title")}
            </span>
            {isVentasOpen ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>
          {isVentasOpen ? (
            <div className="ml-3 flex flex-col gap-1 border-l pl-3">
              {ventasLinks.map((link) => {
                const Icon = link.icon;
                const to = link.buildTo(activeCompany.id);
                const prefix = link.matchPrefix(activeCompany.id);
                const active = link.isActive
                  ? link.isActive(pathname, activeCompany.id)
                  : pathname === to || pathname.startsWith(prefix + "/");
                return (
                  <Link
                    key={link.labelKey}
                    to={to}
                    className={cn(
                      "flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
                      active
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {navLabel(link.labelKey)}
                  </Link>
                );
              })}
            </div>
          ) : null}

          {/* ── Compras collapsible section ─────────────────────────── */}
          <button
            type="button"
            onClick={() => setComprasOpen((s) => !s)}
            className={cn(
              "flex items-center justify-between gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
              comprasActive
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
            )}
            aria-expanded={isComprasOpen}
          >
            <span className="flex items-center gap-2">
              <ShoppingCart className="h-4 w-4" />
              {navLabel("nav.compras.title")}
            </span>
            {isComprasOpen ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>
          {isComprasOpen ? (
            <div className="ml-3 flex flex-col gap-1 border-l pl-3">
              {comprasLinks.map((link) => {
                const Icon = link.icon;
                const to = link.buildTo(activeCompany.id);
                const prefix = link.matchPrefix(activeCompany.id);
                const active =
                  pathname === to || pathname.startsWith(prefix + "/");
                return (
                  <Link
                    key={link.labelKey}
                    to={to}
                    className={cn(
                      "flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
                      active
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {navLabel(link.labelKey)}
                  </Link>
                );
              })}
            </div>
          ) : null}

          {/* ── Tesorería collapsible section ───────────────────────── */}
          <button
            type="button"
            onClick={() => setTesoreriaOpen((s) => !s)}
            className={cn(
              "flex items-center justify-between gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
              tesoreriaActive
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
            )}
            aria-expanded={isTesoreriaOpen}
          >
            <span className="flex items-center gap-2">
              <Wallet className="h-4 w-4" />
              {navLabel("nav.tesoreria.title")}
            </span>
            {isTesoreriaOpen ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>
          {isTesoreriaOpen ? (
            <div className="ml-3 flex flex-col gap-1 border-l pl-3">
              {tesoreriaLinks.map((link) => {
                const Icon = link.icon;
                const to = link.buildTo(activeCompany.id);
                const prefix = link.matchPrefix(activeCompany.id);
                const active =
                  pathname === to || pathname.startsWith(prefix + "/");
                return (
                  <Link
                    key={link.labelKey}
                    to={to}
                    className={cn(
                      "flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
                      active
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {navLabel(link.labelKey)}
                  </Link>
                );
              })}
            </div>
          ) : null}

          {/* ── Cartera ─────────────────────────────────────────────── */}
          <Link
            to="/companies/$companyId/cartera"
            params={{ companyId: activeCompany.id }}
            className={cn(
              "flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
              carteraActive
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
            )}
          >
            <Scale className="h-4 w-4" />
            {navLabel("nav.cartera")}
          </Link>

          {/* ── Archivos (company document store, PF-347) ───────────── */}
          <Link
            to="/companies/$companyId/archivos"
            params={{ companyId: activeCompany.id }}
            className={cn(
              "flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
              archivosActive
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
            )}
          >
            <FolderOpen className="h-4 w-4" />
            {navLabel("nav.archivos")}
          </Link>

          {/* ── Impuestos collapsible section ───────────────────────── */}
          <button
            type="button"
            onClick={() => setImpuestosOpen((s) => !s)}
            className={cn(
              "flex items-center justify-between gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
              impuestosActive
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
            )}
            aria-expanded={isImpuestosOpen}
          >
            <span className="flex items-center gap-2">
              <Percent className="h-4 w-4" />
              {navLabel("nav.impuestos.title")}
            </span>
            {isImpuestosOpen ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>
          {isImpuestosOpen ? (
            <div className="ml-3 flex flex-col gap-1 border-l pl-3">
              {impuestosLinks.map((link) => {
                const Icon = link.icon;
                const to = link.buildTo(activeCompany.id);
                const prefix = link.matchPrefix(activeCompany.id);
                const isCalendar = link.labelKey === "nav.impuestos.calendario";
                const active = isCalendar
                  ? pathname === to
                  : pathname === to || pathname.startsWith(prefix + "/");
                return (
                  <Link
                    key={link.labelKey}
                    to={to}
                    className={cn(
                      "flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
                      active
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {navLabel(link.labelKey)}
                  </Link>
                );
              })}
            </div>
          ) : null}

          {/* ── Inventario collapsible section ───────────────────────── */}
          <button
            type="button"
            onClick={() => setInventarioOpen((s) => !s)}
            className={cn(
              "flex items-center justify-between gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
              inventarioActive
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
            )}
            aria-expanded={isInventarioOpen}
          >
            <span className="flex items-center gap-2">
              <Boxes className="h-4 w-4" />
              {navLabel("nav.inventario.title")}
            </span>
            {isInventarioOpen ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>
          {isInventarioOpen ? (
            <div className="ml-3 flex flex-col gap-1 border-l pl-3">
              {inventarioLinks.map((link) => {
                const Icon = link.icon;
                const to = link.buildTo(activeCompany.id);
                const prefix = link.matchPrefix(activeCompany.id);
                // Dashboard is the bare /inventario route; only count as
                // active when the path is an exact match (otherwise the
                // sub-pages would show two active rows).
                const isExactDashboard =
                  link.labelKey === "nav.inventario.dashboard";
                const active = isExactDashboard
                  ? pathname === to
                  : pathname === to || pathname.startsWith(prefix + "/");
                return (
                  <Link
                    key={link.labelKey}
                    to={to}
                    className={cn(
                      "flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
                      active
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {navLabel(link.labelKey)}
                  </Link>
                );
              })}
            </div>
          ) : null}

          {/* ── Nómina collapsible section ───────────────────────────── */}
          <button
            type="button"
            onClick={() => setNominaOpen((s) => !s)}
            className={cn(
              "flex items-center justify-between gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
              nominaActive
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
            )}
            aria-expanded={isNominaOpen}
          >
            <span className="flex items-center gap-2">
              <ReceiptText className="h-4 w-4" />
              {t("nav.nomina.title")}
            </span>
            {isNominaOpen ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>
          {isNominaOpen ? (
            <div className="ml-3 flex flex-col gap-1 border-l pl-3">
              {nominaLinks.map((link) => {
                const Icon = link.icon;
                const to = link.buildTo(activeCompany.id);
                const prefix = link.matchPrefix(activeCompany.id);
                const active =
                  pathname === to || pathname.startsWith(prefix + "/");
                return (
                  <Link
                    key={link.labelKey}
                    to={to}
                    className={cn(
                      "flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
                      active
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {t(link.labelKey)}
                  </Link>
                );
              })}
            </div>
          ) : null}

          {/* ── Contabilidad collapsible section ─────────────────────── */}
          <button
            type="button"
            onClick={() => setContabilidadOpen((s) => !s)}
            className={cn(
              "flex items-center justify-between gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
              contabilidadActive
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
            )}
            aria-expanded={isContabilidadOpen}
          >
            <span className="flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              {t("nav.contabilidad.title")}
            </span>
            {isContabilidadOpen ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>
          {isContabilidadOpen ? (
            <div className="ml-3 flex flex-col gap-1 border-l pl-3">
              {contabilidadLinks.map((link) => {
                const Icon = link.icon;
                const to = link.buildTo(activeCompany.id);
                const prefix = link.matchPrefix(activeCompany.id);
                const active =
                  pathname === to || pathname.startsWith(prefix + "/");
                return (
                  <Link
                    key={link.labelKey}
                    to={to}
                    className={cn(
                      "flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
                      active
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {t(link.labelKey)}
                  </Link>
                );
              })}
            </div>
          ) : null}

          {/* ── Reportes collapsible section ──────────────────────────── */}
          <button
            type="button"
            onClick={() => setReportesOpen((s) => !s)}
            className={cn(
              "flex items-center justify-between gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
              reportesActive
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
            )}
            aria-expanded={isReportesOpen}
          >
            <span className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              {t("nav.reportes.title")}
            </span>
            {isReportesOpen ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>
          {isReportesOpen ? (
            <div className="ml-3 flex flex-col gap-1 border-l pl-3">
              {reportesLinks.map((link) => {
                const Icon = link.icon;
                const to = link.buildTo(activeCompany.id);
                const prefix = link.matchPrefix(activeCompany.id);
                const active =
                  pathname === to || pathname.startsWith(prefix + "/");
                return (
                  <Link
                    key={link.labelKey}
                    to={to}
                    className={cn(
                      "flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
                      active
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {t(link.labelKey)}
                  </Link>
                );
              })}
            </div>
          ) : null}

          <Separator className="my-2" />
          <div className="px-3 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            {t("nav.catalogos.title")}
          </div>
          {companyLinks.map((link) => {
            const Icon = link.icon;
            const to = link.buildTo(activeCompany.id);
            const prefix = link.matchPrefix(activeCompany.id);
            const active = pathname === to || pathname.startsWith(prefix + "/");
            return (
              <Link
                key={link.labelKey}
                to={to}
                className={cn(
                  "flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                  active
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                )}
              >
                <Icon className="h-4 w-4" />
                {t(link.labelKey)}
              </Link>
            );
          })}
        </>
      ) : null}
    </nav>
  );
}

/** Persistent desktop sidebar. */
export function AppSidebar() {
  return (
    <aside className="hidden w-56 shrink-0 border-r bg-card lg:flex lg:flex-col">
      <AppNavigation />
    </aside>
  );
}
